// Room AC–Fan cycle — the words and numbers the card and the editor show.
// The engine is the SERVER's (Home Automation api/new/roomCycle.js); this file
// only describes what it reports. Keep DEFAULTS / limits in step with its
// normalise() — the server clamps anyway, so a mismatch only shows a value the
// server will correct, never a wrong command.
//
//   state ''        off
//         'ac'      AC on (fan at FanDuringAc) for AcMin
//         'fan'     AC off, fan at FanSpeed for FanMin
//         'acwait'  AC PROTECTION — the AC went off < MinOffMin ago; fan keeps going
//         'morning' after the morning stop: AC off, fan at Morning.FanSpeed until FanOffAt

export const FAN_OFF = 10;
export const FAN_SPEEDS = [20, 40, 60, 80, 100];
export const MIN_OFF_FLOOR = 3;

export const DEFAULTS = {
  Ac: null,
  Fan: null,
  AcMin: 40,
  FanMin: 20,
  FanSpeed: 80,
  FanDuringAc: FAN_OFF,
  MinOffMin: MIN_OFF_FLOOR,
  HandPress: 'keep',
  Morning: { On: false, StopAt: '06:00', FanSpeed: 60, FanOffOn: true, FanOffAt: '07:00' },
  Notify: true,
};

export const PRESETS = [
  { key: 'sleep', label: 'Sleep', sub: 'AC 40 · Fan 20', AcMin: 40, FanMin: 20, FanSpeed: 80 },
  { key: 'hot', label: 'Hot night', sub: 'AC 50 · Fan 10', AcMin: 50, FanMin: 10, FanSpeed: 100 },
  { key: 'mild', label: 'Mild', sub: 'AC 30 · Fan 30', AcMin: 30, FanMin: 30, FanSpeed: 60 },
];

export const LOADS = [
  { value: '', label: 'Not set' },
  { value: 'ac', label: 'AC' },
  { value: 'fan', label: 'Fan' },
  { value: 'light', label: 'Light' },
  { value: 'geyser', label: 'Geyser' },
  { value: 'other', label: 'Other' },
];

/** Server settings (or null) → a complete editable copy. */
export function withDefaults(s) {
  const src = s || {};
  return {
    ...DEFAULTS,
    ...src,
    Morning: { ...DEFAULTS.Morning, ...(src.Morning || {}) },
  };
}

export const speedLabel = (v) => (Number(v) > FAN_OFF ? `${Number(v)}%` : 'Off');

/** 125 → "2 min", 3725 → "1 h 2 min", 40 → "40 s". */
export function duration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s} s`;
  const m = Math.ceil(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

export const isRunning = (state) => !!state;

/** The headline for a state, with the time left. */
export function phaseLine(state, settings, secondsLeft) {
  const s = withDefaults(settings);
  const left = secondsLeft == null ? '' : ` — ${duration(secondsLeft)} left`;
  switch (state) {
    case 'ac': return `AC on${s.FanDuringAc > FAN_OFF ? `, fan ${speedLabel(s.FanDuringAc)}` : ''}${left}`;
    case 'fan': return `Fan ${speedLabel(s.FanSpeed)}, AC resting${left}`;
    case 'acwait': return `AC protection — AC starts in ${duration(secondsLeft || 0)}`;
    case 'morning': return `Morning: fan ${speedLabel(s.Morning.FanSpeed)}${secondsLeft == null ? '' : ` until ${s.Morning.FanOffAt}`}`;
    default: return 'Off';
  }
}

/** What comes after the current phase. */
export function nextLine(state, settings) {
  const s = withDefaults(settings);
  switch (state) {
    case 'ac': return `Next: fan ${speedLabel(s.FanSpeed)} for ${s.FanMin} min`;
    case 'fan':
    case 'acwait': return `Next: AC for ${s.AcMin} min`;
    case 'morning': return s.Morning.FanOffOn ? `Then fan off at ${s.Morning.FanOffAt} — cycle ends` : '';
    default: return '';
  }
}

/** One line for the whole program: "AC 40 min · Fan 80% 20 min · morning 06:00". */
export function programLine(settings) {
  const s = withDefaults(settings);
  const parts = [`AC ${s.AcMin} min`, `Fan ${speedLabel(s.FanSpeed)} ${s.FanMin} min`];
  if (s.Morning.On) {
    parts.push(s.Morning.FanSpeed > FAN_OFF && s.Morning.FanOffOn
      ? `${s.Morning.StopAt} fan only → ${s.Morning.FanOffAt} off`
      : `ends ${s.Morning.StopAt}`);
  }
  return parts.join(' · ');
}

/** 0..1 through the current phase, or null when it has no fixed length. */
export function progress(secondsLeft, phaseSeconds) {
  if (secondsLeft == null || !phaseSeconds) return null;
  return Math.min(1, Math.max(0, 1 - secondsLeft / phaseSeconds));
}

const key = (t) => (t ? `${t.UniqueNumber}/${t.Output}` : '');
export const targetKey = key;
export const sameTarget = (a, b) => !!a && !!b && key(a) === key(b);

/** The editor's pick list label: "Bedroom AC · 30A switch". */
export const targetLabel = (o) => (o ? [o.Name, o.DeviceName && o.DeviceName !== o.Name ? o.DeviceName : '']
  .filter(Boolean).join(' · ') : '');

/**
 * Fill in the AC / fan when the room has exactly one of each (most rooms), so a
 * first-time user only has to press Save.
 */
export function autoPick(settings, candidates) {
  const s = withDefaults(settings);
  const ac = (candidates && candidates.ac) || [];
  const fan = (candidates && candidates.fan) || [];
  const pick = (cur, list) => {
    if (cur && list.some((o) => sameTarget(o, cur))) return cur;
    return list.length ? { UniqueNumber: list[0].UniqueNumber, Output: list[0].Output } : null;
  };
  return { ...s, Ac: pick(s.Ac, ac), Fan: pick(s.Fan, fan) };
}

/** -> '' when the editor may save, else what to fix. */
export function settingsProblem(s) {
  if (!s.Ac) return 'Choose the AC';
  if (!s.Fan) return 'Choose the fan';
  if (sameTarget(s.Ac, s.Fan)) return 'The AC and the fan must be different switches';
  if (s.Morning.On && s.Morning.FanOffOn && s.Morning.FanSpeed > FAN_OFF && s.Morning.StopAt === s.Morning.FanOffAt) {
    return 'The fan-off time must differ from the morning stop time';
  }
  return '';
}

/** A server error → words for the user. */
export function cycleError(e, fallback = 'Something went wrong') {
  return e?.response?.data?.error || e?.message || fallback;
}
